/**
 * ============================================================================
 * SHAPE CONSTANTS
 * ============================================================================
 *
 * Central location for all shape-related constants including:
 * - Shape type definitions
 * - Shape feature flags
 * - Border type definitions
 * - Border type groupings
 * - Default values
 *
 * These constants are used throughout the shapes architecture:
 * - ShapeConfigService (services/shapeConfig.js)
 * - ShapeOptionsModel (models/ShapeOptionsModel.js)
 * - Shape style utilities (utils/shapeStyles.js)
 * - Shape components (components/modules/ShapeOptions.vue, ShapesWrapper.vue)
 * ============================================================================
 */

/**
 * Shape type constants
 * SHAPE_TYPE_RECTANGLE is undefined to represent the default/null state
 */
export const SHAPE_TYPE_RECTANGLE = undefined;
export const SHAPE_TYPE_CIRCLE = 'circle';
export const SHAPE_TYPE_BOX = 'box';
export const SHAPE_TYPE_LINE = 'line';
export const SHAPE_TYPE_ARROW = 'arrow';
export const SHAPE_TYPE_PENTAGON = 'pentagon';
export const SHAPE_TYPE_ROUNDED_RECTANGLE = 'rounded_rectangle';
export const SHAPE_TYPE_TITLE_BUBBLE = 'title_bubble';
export const SHAPE_TYPE_SPEECH_BUBBLE_LOWER_RIGHT = 'speech_bubble_lower_right';
export const SHAPE_TYPE_SPEECH_BUBBLE_LOWER_LEFT = 'speech_bubble_lower_left';
export const SHAPE_TYPE_LIST = [
  SHAPE_TYPE_CIRCLE,
  SHAPE_TYPE_BOX,
  SHAPE_TYPE_LINE,
  SHAPE_TYPE_ARROW,
  SHAPE_TYPE_PENTAGON,
  SHAPE_TYPE_ROUNDED_RECTANGLE,
  SHAPE_TYPE_TITLE_BUBBLE,
  SHAPE_TYPE_SPEECH_BUBBLE_LOWER_RIGHT,
  SHAPE_TYPE_SPEECH_BUBBLE_LOWER_LEFT,
];

export const SHAPE_LEGACY_BORDER_WIDTH = 37;

/**
 * Feature flags for shape capabilities
 * Used by ShapeConfigService to determine which styling features each shape supports
 */
export const SHAPE_FEATURES = {
  HAS_FILL: 'hasFill',
  HAS_BORDER: 'hasBorder',
  HAS_CORNER_RADIUS: 'hasCornerRadius',
  HAS_DROP_SHADOW: 'hasDropShadow',
  USES_BOX_SHADOW: 'usesBoxShadow',
  USES_DROP_SHADOW: 'usesDropShadow',
  HAS_SVG_MASK: 'hasSvgMask',
  SUPPORTS_STANDARD_BORDER: 'supportsStandardBorder',
};

/**
 * Border type constants
 * Used to define allowed border types for shapes
 */
export const BORDER_TYPE_NONE = '';
export const BORDER_TYPE_SOLID = 'solid';
export const BORDER_TYPE_CLOUDS = 'clouds';
export const BORDER_TYPE_DOTTED = 'dotted';
export const BORDER_TYPE_DASHED = 'dashed';
export const BORDER_TYPE_DOUBLE = 'double';

/**
 * Predefined sets of allowed border types for different shape categories
 */
export const ALLOWED_BORDER_TYPES = {
  // Rectangle and Box shapes support all border types
  FULL: [
    BORDER_TYPE_NONE,
    BORDER_TYPE_SOLID,
    BORDER_TYPE_CLOUDS,
    BORDER_TYPE_DOTTED,
    BORDER_TYPE_DASHED,
    BORDER_TYPE_DOUBLE,
  ],
  // Circle, Pentagon, and other complex shapes support basic border types
  BASIC: [
    BORDER_TYPE_NONE,
    BORDER_TYPE_SOLID,
    BORDER_TYPE_CLOUDS,
  ],
  // Line and Arrow shapes don't support borders
  NONE: [],
};

// Shape shadow property defaults. Add this as a default to modules that has 'ShapeWrapper'
// implemented to use the shape properties.
export const DEFAULT_SHAPE_SHADOW = {
  blur: 0,
  color: '#000000',
  offset_x: 0,
  offset_y: 0,
  opacity: 100,
  spread: 0,
};

/**
 *  Default border radius used in service/shapeConfig.
 * */
export const DEFAULT_BORDER_RADIUS = '0px 0px 0px 0px';

/**
 * Default shape options structure
 * Used by ShapeOptionsModel.getDefaults()
 */
export const DEFAULT_SHAPE_OPTIONS = {
  shape: undefined, // Rectangle (default)
  shape_type: null, // Legacy property
  shape_background_color: null,
  shape_background_opacity: null,
  shape_stroke_width: 0,
  shape_stroke_color: null,
  shape_stroke_opacity: 100,
  shape_radius: DEFAULT_BORDER_RADIUS,
  shape_pattern: 'none',
  shape_shadow: DEFAULT_SHAPE_SHADOW,
  border: null,
};
